import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { MiscItem } from '../types';
import { validateWeight } from '../utils/weightConversion';

const COLLECTION_NAME = 'miscItems';

/**
 * Get all misc items
 */
export const getMiscItems = async (): Promise<MiscItem[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('isActive', 'desc'),
      orderBy('category', 'asc'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as MiscItem[];
  } catch (error) {
    console.error('Error fetching misc items:', error);
    throw new Error('Failed to fetch misc items');
  }
};

/**
 * Get active misc items only
 */
export const getActiveMiscItems = async (): Promise<MiscItem[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('isActive', '==', true),
      orderBy('category', 'asc'),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as MiscItem[];
  } catch (error) {
    console.error('Error fetching active misc items:', error);
    throw new Error('Failed to fetch active misc items');
  }
};

/**
 * Get misc items by category
 */
export const getMiscItemsByCategory = async (category: string): Promise<MiscItem[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    })) as MiscItem[];
  } catch (error) {
    console.error('Error fetching misc items by category:', error);
    throw new Error('Failed to fetch misc items by category');
  }
};

/**
 * Create a new misc item
 */
export const createMiscItem = async (itemData: Omit<MiscItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...itemData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating misc item:', error);
    throw new Error('Failed to create misc item');
  }
};

/**
 * Update an existing misc item
 */
export const updateMiscItem = async (id: string, updates: Partial<MiscItem>): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating misc item:', error);
    throw new Error('Failed to update misc item');
  }
};

/**
 * Delete a misc item
 */
export const deleteMiscItem = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  } catch (error) {
    console.error('Error deleting misc item:', error);
    throw new Error('Failed to delete misc item');
  }
};

/**
 * Toggle active status of a misc item
 */
export const toggleMiscItemStatus = async (id: string, isActive: boolean): Promise<void> => {
  try {
    await updateMiscItem(id, { isActive });
  } catch (error) {
    console.error('Error toggling misc item status:', error);
    throw new Error('Failed to update misc item status');
  }
};

/**
 * Get available categories
 */
export const getMiscItemCategories = async (): Promise<string[]> => {
  try {
    const items = await getMiscItems();
    const categories = [...new Set(items.map(item => item.category).filter(Boolean))];
    return categories.sort();
  } catch (error) {
    console.error('Error fetching misc item categories:', error);
    return [];
  }
};

/**
 * Validate misc item data
 */
export const validateMiscItem = (item: Partial<MiscItem>): string[] => {
  const errors: string[] = [];
  
  if (!item.name?.trim()) {
    errors.push('Item name is required');
  }
  
  if (!item.description?.trim()) {
    errors.push('Item description is required');
  }
  
  if (!item.weight || item.weight <= 0) {
    errors.push('Weight must be greater than 0');
  }
  
  if (!item.weightUnit) {
    errors.push('Weight unit is required');
  }
  
  if (item.weight && item.weightUnit) {
    const weightErrors = validateWeight(item.weight, item.weightUnit);
    errors.push(...weightErrors);
  }
  
  if (!item.value || item.value <= 0) {
    errors.push('Value must be greater than 0');
  }
  
  if (item.value && item.value > 10000) {
    errors.push('Value seems unusually high for a misc item');
  }
  
  if (!item.originCountry?.trim()) {
    errors.push('Origin country is required');
  }
  
  if (item.originCountry && item.originCountry.length !== 2) {
    errors.push('Origin country should be a 2-character code (e.g., US, CA, JM)');
  }
  
  // Validate HS Code format if provided
  if (item.hsCode && !/^\d{6,10}$/.test(item.hsCode)) {
    errors.push('HS Code should be 6-10 digits');
  }
  
  return errors;
};

/**
 * Get default misc items for setup
 */
export const getDefaultMiscItems = (): Omit<MiscItem, 'id' | 'createdAt' | 'updatedAt'>[] => {
  return [
    {
      name: 'Thank You Card',
      description: 'Personalized thank you card with JamPack\'d branding',
      weight: 1,
      weightUnit: 'oz',
      value: 0.50,
      hsCode: '490199', // Printed matter not elsewhere specified
      originCountry: 'US',
      manufacturer: 'JamPack\'d',
      isActive: true,
      category: 'cards',
    },
    {
      name: 'Gift Note',
      description: 'Handwritten gift message card',
      weight: 0.5,
      weightUnit: 'oz',
      value: 0.25,
      hsCode: '490199',
      originCountry: 'US',
      manufacturer: 'JamPack\'d',
      isActive: true,
      category: 'cards',
    },
    {
      name: 'Recipe Card',
      description: 'Traditional Jamaican recipe card',
      weight: 0.5,
      weightUnit: 'oz',
      value: 0.30,
      hsCode: '490199',
      originCountry: 'JM',
      manufacturer: 'JamPack\'d',
      isActive: true,
      category: 'cards',
    },
    {
      name: 'Product Information Leaflet',
      description: 'Information about product origins and usage',
      weight: 0.3,
      weightUnit: 'oz',
      value: 0.10,
      hsCode: '490199',
      originCountry: 'US',
      manufacturer: 'JamPack\'d',
      isActive: true,
      category: 'documentation',
    }
  ];
};